import { useState } from 'react';
import styled from 'styled-components';
import {
  useAddGeoSheetsFromRdMutation,
  useAddGeoSheetsMutation,
  useDeleteGeoExtraSheetMutation,
  useGetGeoRdDocumentsQuery,
  usePatchGeoSheetMutation,
} from '@shared/api';
import {
  SHEET_ROLES,
  sheetRoleLabel,
  type GeoScheme,
  type GeoSheetRole,
  type GeoSheetSource,
} from '@entities/geodesy';
import { Button } from '@shared/ui/Button';
import { FileDropZone } from '@shared/ui/FileDropZone';
import { Select } from '@shared/ui/Input';
import { IconTrash } from '@shared/ui/icons';
import {
  CellSelect,
  DataTable,
  ErrorText,
  FieldGrid,
  FieldLabel,
  Hint,
  IconButton,
  LinkButton,
  Muted,
  Notice,
  Panel,
  PanelHead,
  PanelTitle,
  Stack,
  TableWrap,
  useAction,
} from '@widgets/GeoSchemes';
import { revisionName } from './RdSheet';
import { useWizard } from './wizard';

const ACCEPT_SHEET = '.pdf,.dxf';
/** Листов комплекта кроме основного — не больше (как на сервере). */
const MAX_EXTRA = 5;
/** Значение выбора «роль найти заново по листу». */
const AUTO_ROLE = '';

const Code = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Sub = styled.h4`
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Why = styled.div`
  margin-top: ${({ theme }) => theme.spacing(0.5)};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/** Шифр листа из основной надписи, иначе имя файла. */
function sheetName(s: GeoSheetSource): string {
  return s.stamp.designation?.trim() || s.file_name;
}

/**
 * «Листы комплекта» (шаг 1, ТЗ AI-GEO-2026 v2.1): конструктив разнесён на 2–3
 * листа — схема расположения (основной лист ИС, его привязывают к МСК на шаге
 * 2), чертёж марки (опалубка, разрез с высотами ярусов), лист анкерных блоков.
 * Роль листа программа узнаёт по наименованию и заголовкам видов; ошиблась —
 * геодезист выбирает сам. Собирают конструкцию на шаге 6 «Собрать из листов».
 */
export function SheetSetPanel() {
  const { scheme } = useWizard();
  if (!scheme.multi_sheet || !scheme.sheet) return null;
  return <SheetSet scheme={scheme} primary={scheme.sheet} />;
}

function SheetSet({ scheme, primary }: { scheme: GeoScheme; primary: GeoSheetSource }) {
  const { readOnly, goTo } = useWizard();
  const extra = scheme.sheets ?? [];
  const [addFiles] = useAddGeoSheetsMutation();
  const [patch] = usePatchGeoSheetMutation();
  const [remove] = useDeleteGeoExtraSheetMutation();
  const adding = useAction();
  const editing = useAction();
  const removing = useAction();
  const room = MAX_EXTRA - extra.length;

  function onFiles(files: File[]) {
    if (files.length > room) {
      window.alert(`Листов комплекта — не больше ${MAX_EXTRA}, кроме основного.`);
      return;
    }
    void adding.run(
      () => addFiles({ id: scheme.id, files }).unwrap(),
      'Листы не загрузились. Проверьте формат: PDF или DXF.',
    );
  }

  function setRole(s: GeoSheetSource, value: string) {
    const role = (SHEET_ROLES.find((r) => r.value === value)?.value ?? null) as GeoSheetRole | null;
    void editing.run(
      () => patch({ id: scheme.id, fileId: s.file_id, role }).unwrap(),
      'Роль листа не сохранилась.',
    );
  }

  function setPage(s: GeoSheetSource, page: number) {
    void editing.run(
      () => patch({ id: scheme.id, fileId: s.file_id, page }).unwrap(),
      'Страницу не удалось переключить.',
    );
  }

  function drop(s: GeoSheetSource) {
    if (!window.confirm(`Убрать лист «${sheetName(s)}» из комплекта?`)) return;
    void removing.run(
      () => remove({ id: scheme.id, fileId: s.file_id }).unwrap(),
      'Лист не удалось убрать.',
    );
  }

  const rows: { sheet: GeoSheetSource; primary: boolean }[] = [
    { sheet: primary, primary: true },
    ...extra.map((s) => ({ sheet: s, primary: false })),
  ];
  const primaryNotLayout = primary.role != null && primary.role !== 'layout_plan';
  const hasDetail = extra.some((s) => s.role === 'structural_detail' || s.role == null);

  return (
    <Panel aria-labelledby="geo-sheet-set-title">
      <PanelHead>
        <PanelTitle id="geo-sheet-set-title">Листы комплекта</PanelTitle>
        <Muted>{rows.length} л.</Muted>
      </PanelHead>
      <Stack $gap={4}>
        <Hint>
          Фундамент на промышленном объекте начерчен на 2–3 листах: схема расположения (основной
          лист — оси, координаты, отметка низа), чертёж марки (опалубка, разрез с высотами) и лист
          анкерных блоков. Добавьте их — программа сама посадит конструктив на схему, повернёт и
          подставит отметки ярусов.
        </Hint>
        {primaryNotLayout && (
          <Notice $tone="warning" role="status">
            Основной лист — {sheetRoleLabel(primary.role).toLowerCase()}, а не схема расположения:
            координаты конструкции возьмутся с выносок схемы, но подложкой чертежа сборка не станет.
            Сделайте схему расположения основным листом — загрузите её выше.
          </Notice>
        )}
        <TableWrap>
          <DataTable>
            <thead>
              <tr>
                <th scope="col">Лист</th>
                <th scope="col">Роль</th>
                <th scope="col">Страница</th>
                {!readOnly && <th scope="col" aria-label="Действия" />}
              </tr>
            </thead>
            <tbody>
              {rows.map(({ sheet: s, primary: isPrimary }) => (
                <tr key={s.file_id}>
                  <td>
                    <Code>{sheetName(s)}</Code>
                    {isPrimary && <Muted> · основной лист ИС</Muted>}
                    <Why>
                      {s.stamp.sheet_title?.trim() || s.file_name}
                      {s.rd
                        ? ` · реестр РД: ${revisionName(s.rd.rev_label, s.rd.revision_no)}`
                        : ''}
                    </Why>
                  </td>
                  <td>
                    {readOnly ? (
                      sheetRoleLabel(s.role)
                    ) : (
                      <CellSelect
                        aria-label={`Роль листа ${sheetName(s)}`}
                        value={s.role_by_user ? (s.role ?? AUTO_ROLE) : AUTO_ROLE}
                        disabled={editing.busy}
                        onChange={(e) => setRole(s, e.target.value)}
                      >
                        <option value={AUTO_ROLE}>
                          {s.role ? `${sheetRoleLabel(s.role)} (узнана)` : 'не узнана — выберите'}
                        </option>
                        {SHEET_ROLES.map((r) => (
                          <option key={r.value} value={r.value} title={r.hint}>
                            {r.label}
                          </option>
                        ))}
                      </CellSelect>
                    )}
                    {s.role_reason && <Why>{s.role_reason}</Why>}
                  </td>
                  <td>
                    {!isPrimary && s.pages > 1 && !readOnly ? (
                      <CellSelect
                        aria-label={`Страница листа ${sheetName(s)}`}
                        value={s.page}
                        disabled={editing.busy}
                        onChange={(e) => setPage(s, Number(e.target.value))}
                      >
                        {Array.from({ length: s.pages }, (_, i) => (
                          <option key={i} value={i}>
                            {i + 1} из {s.pages}
                          </option>
                        ))}
                      </CellSelect>
                    ) : s.pages > 1 ? (
                      `${s.page + 1} из ${s.pages}`
                    ) : (
                      '—'
                    )}
                  </td>
                  {!readOnly && (
                    <td>
                      {!isPrimary && (
                        <IconButton
                          type="button"
                          $danger
                          aria-label={`Убрать лист ${sheetName(s)}`}
                          title="Убрать из комплекта"
                          disabled={removing.busy}
                          onClick={() => drop(s)}
                        >
                          <IconTrash size={16} />
                        </IconButton>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </DataTable>
        </TableWrap>
        {editing.error && <ErrorText role="alert">{editing.error}</ErrorText>}
        {removing.error && <ErrorText role="alert">{removing.error}</ErrorText>}

        {!readOnly && room > 0 && (
          <Stack $gap={3}>
            <Sub>Добавить листы</Sub>
            <RdSheetsPicker scheme={scheme} room={room} />
            <FileDropZone
              accept={ACCEPT_SHEET}
              compact
              multiple
              disabled={adding.busy}
              primary={
                adding.busy
                  ? 'Загружаем и осматриваем листы…'
                  : 'Чертёж марки, лист анкерных блоков'
              }
              hint="PDF или DXF, можно несколько сразу"
              onFiles={onFiles}
            />
            {adding.error && <ErrorText role="alert">{adding.error}</ErrorText>}
          </Stack>
        )}
        {extra.length > 0 && hasDetail && (
          <Hint>
            Листы на месте — соберите из них конструкцию{' '}
            <LinkButton type="button" onClick={() => goTo(6)}>
              Собрать из листов — шаг 6 →
            </LinkButton>
          </Hint>
        )}
      </Stack>
    </Panel>
  );
}

/** Листы комплекта из реестра РД объекта: документ и файл (несколько — по одному). */
function RdSheetsPicker({ scheme, room }: { scheme: GeoScheme; room: number }) {
  const skip = !scheme.project_id;
  const { data } = useGetGeoRdDocumentsQuery(scheme.id, { skip });
  const [take] = useAddGeoSheetsFromRdMutation();
  const taking = useAction();
  const [docId, setDocId] = useState('');
  const [file, setFile] = useState('');
  const docs = data?.documents ?? [];
  if (skip || docs.length === 0) return null;
  const doc = docs.find((d) => d.document_id === docId) ?? null;
  const files = doc?.files ?? [];
  const chosen = files.length === 1 ? files[0] : file;

  const run = () => {
    if (!doc || !chosen || room <= 0) return;
    void taking
      .run(
        () =>
          take({
            id: scheme.id,
            items: [{ revision_id: doc.revision_id, filename: chosen }],
          }).unwrap(),
        'Лист из реестра РД не взят.',
      )
      .then((ok) => {
        if (ok) {
          setDocId('');
          setFile('');
        }
      });
  };

  return (
    <Stack $gap={2}>
      <FieldGrid $min={240}>
        <FieldLabel>
          Из реестра РД ПТО
          <Select
            value={docId}
            onChange={(e) => {
              setDocId(e.target.value);
              setFile('');
            }}
          >
            <option value="">— документ —</option>
            {docs.map((d) => (
              <option key={d.document_id} value={d.document_id}>
                {d.mark ? `${d.mark} — ` : ''}
                {d.code} «{d.title}» · {revisionName(d.rev_label, d.revision_no)}
              </option>
            ))}
          </Select>
        </FieldLabel>
        {files.length > 1 && (
          <FieldLabel>
            Файл листа
            <Select value={file} onChange={(e) => setFile(e.target.value)}>
              <option value="">— выберите —</option>
              {files.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </Select>
          </FieldLabel>
        )}
      </FieldGrid>
      <div>
        <Button
          type="button"
          $variant="secondary"
          $size="sm"
          onClick={run}
          disabled={taking.busy || !doc || !chosen}
        >
          {taking.busy ? 'Берём лист…' : 'Добавить лист из реестра'}
        </Button>
      </div>
      {taking.error && <ErrorText role="alert">{taking.error}</ErrorText>}
    </Stack>
  );
}
